import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { SystemSettings } from '@/types';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Settings, Bell, Shield, Database } from 'lucide-react';

const DEFAULT_SETTINGS: Omit<SystemSettings, 'id' | 'updatedAt'> = {
  notifications: {
    maintenanceEnabled: true,
    releaseEnabled: true,
  },
  security: {
    twoFactorRecommended: false,
    sessionTimeout: '1時間',
  },
};

export const AdminSettings = () => {
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const settingsRef = doc(db, 'systemSettings', 'main');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        setSettings({
          id: settingsDoc.id,
          ...settingsDoc.data(),
        } as SystemSettings);
      } else {
        // デフォルト設定を作成
        const defaultSettings: SystemSettings = {
          id: 'main',
          ...DEFAULT_SETTINGS,
          updatedAt: Timestamp.now(),
        };
        
        await setDoc(settingsRef, {
          ...DEFAULT_SETTINGS,
          updatedAt: Timestamp.now(),
        });
        
        setSettings(defaultSettings);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      alert('設定の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;
    
    setSaving(true);
    
    try {
      const settingsRef = doc(db, 'systemSettings', 'main');
      
      await setDoc(settingsRef, {
        notifications: settings.notifications,
        security: settings.security,
        updatedAt: Timestamp.now(),
      });
      
      alert('設定を保存しました。');
      fetchSettings();
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('設定の保存に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  const updateNotificationSetting = (key: keyof SystemSettings['notifications'], value: boolean) => {
    if (!settings) return;
    
    setSettings({
      ...settings,
      notifications: {
        ...settings.notifications,
        [key]: value,
      },
    });
  };

  const updateSecuritySetting = <K extends keyof SystemSettings['security']>(
    key: K,
    value: SystemSettings['security'][K]
  ) => {
    if (!settings) return;
    
    setSettings({
      ...settings,
      security: {
        ...settings.security,
        [key]: value,
      },
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!settings) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-gray-500">設定の読み込みに失敗しました。</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* ページヘッダー */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">システム設定</h1>
            <p className="text-gray-600 mt-1">アプリケーション全体の設定</p>
          </div>
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? '保存中...' : '設定を保存'}
          </button>
        </div>

        {/* 設定カテゴリー */}
        <div className="space-y-6">
          {/* 通知設定 */}
          <div className="card">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Bell className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">通知設定</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">システムメンテナンス通知</p>
                  <p className="text-sm text-gray-500">
                    メンテナンス時に全ユーザーへ通知を送信
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.notifications.maintenanceEnabled}
                    onChange={(e) => updateNotificationSetting('maintenanceEnabled', e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">新機能リリース通知</p>
                  <p className="text-sm text-gray-500">
                    新機能がリリースされた際に通知を送信
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.notifications.releaseEnabled}
                    onChange={(e) => updateNotificationSetting('releaseEnabled', e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>

          {/* セキュリティ設定 */}
          <div className="card">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">セキュリティ設定</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">二段階認証を推奨</p>
                  <p className="text-sm text-gray-500">
                    全ユーザーに二段階認証の設定を促す
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.security.twoFactorRecommended}
                    onChange={(e) => updateSecuritySetting('twoFactorRecommended', e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">セッションタイムアウト</p>
                  <p className="text-sm text-gray-500">
                    一定時間操作がない場合に自動ログアウト
                  </p>
                </div>
                <select
                  className="input w-32"
                  value={settings.security.sessionTimeout}
                  onChange={(e) => updateSecuritySetting('sessionTimeout', e.target.value)}
                >
                  <option>30分</option>
                  <option>1時間</option>
                  <option>2時間</option>
                  <option>無効</option>
                </select>
              </div>
            </div>
          </div>

          {/* データ管理 */}
          <div className="card">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <Database className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">データ管理</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="font-medium text-gray-900 mb-2">バックアップ</p>
                <div className="flex items-center gap-3">
                  <button className="btn-primary">
                    手動バックアップ実行
                  </button>
                  <span className="text-sm text-gray-500">
                    最終バックアップ: {settings.updatedAt && new Date(settings.updatedAt.toMillis()).toLocaleString('ja-JP')}
                  </span>
                </div>
              </div>

              <div>
                <p className="font-medium text-gray-900 mb-2">データエクスポート</p>
                <button className="btn-secondary">
                  全データをエクスポート
                </button>
              </div>
            </div>
          </div>

          {/* システム情報 */}
          <div className="card">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Settings className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">システム情報</h2>
            </div>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">バージョン</span>
                <span className="font-mono text-gray-900">2.0.0</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">環境</span>
                <span className="font-mono text-gray-900">Production</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Firebase プロジェクト</span>
                <span className="font-mono text-gray-900">side-pocket-sls</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">最終更新</span>
                <span className="font-mono text-gray-900">
                  {settings.updatedAt && new Date(settings.updatedAt.toMillis()).toLocaleString('ja-JP')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};
